import { Component, Host, h, Prop, Element, State, Event, EventEmitter } from '@stencil/core';

export interface AhaSlider extends HTMLAhaSliderElement {}

/**
 * Can pass in either a min and max or a list of items
 * The list of items will generate a set of ticks along the range
 * The value is returned through the "clickedItem" EventEmitter
 * It is only returned when the handle on the range is released or stops movement
 *
 * @slot label - Contents of the label container.
 * @slot units - Contents of the units container.
 */
@Component({
  tag: 'aha-slider',
  styleUrl: 'aha-slider.css',
  shadow: true,
})
export class AhaSlider {
  @Element() host!: HTMLAhaSliderElement;
  /** Max value on the input slider. Do not set if you are using ticks */
  @Prop({ reflect: true }) max: number | undefined = undefined;
  /** Min value on the input slider. Do not set if you are using ticks */
  @Prop({ reflect: true }) min: number | undefined = undefined;
  /** Step value on the input slider. Do not set if you are using ticks */
  @Prop({ reflect: true }) step: number | undefined = undefined;

  /** The initial value that the input will be set to */
  @Prop({ reflect: true }) startValue: number | undefined = undefined;

  /** Array of tick values for the slider to snap to on change of input. i.e. "[1, 2, 4, 5, 10]" */
  @Prop() ticks: string | undefined = undefined;

  /** Where to show the optional label, 'above' or 'below'. Omit for no label. */
  @Prop({ reflect: true }) labeled: undefined | 'above' | 'below' = undefined;

  /** Key: Tick value, Value: tick percentage for slider input placement */
  @State() ticksArray: number[] = [];

  @State() inputSliderValue: number = 0;
  @State() selectedTickValue: number | undefined = undefined;
  @State() shouldRenderTicks: boolean = false;

  @Event({
    eventName: 'aha-slider:clicked',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  clickedItem?: EventEmitter<number>; // returns the selected item from the array

  private inputElement?: HTMLElement;

  private parseArrayProp(ticksString: string) {
    if (ticksString)
      this.ticksArray = JSON.parse(ticksString).sort((a: number, b: number) => a - b);
  }

  /* use a linear gradient to fill the bar as the value changes */
  private setProgressBar() {
    const minValue = this.ticks ? 0 : this.min ? this.min : 0;
    const maxValue = this.ticks ? this.ticksArray.length - 1 : this.max ? this.max : 100;
    const inputPercent =
      this.inputSliderValue !== 0
        ? this.calculateValuePercentage(this.inputSliderValue, maxValue, minValue) * 100
        : 0;

    this.inputElement?.style.setProperty('--aha-slider--value', inputPercent.toString());
  }

  /* calculate the equivalent 0-1 percentage of a given value from the items array */
  private calculateValuePercentage = (value: number, maxValue: any, minValue: any) =>
    (value - minValue) / (maxValue - minValue);

  private handleInput = (event: any) => {
    this.selectedTickValue = this.ticksArray[event.target.value];
    this.inputSliderValue = event.target.value;

    // Must use selectedTickValue (if it exists) because inputSliderValue may be an index when ticks are used.
    this.clickedItem?.emit(Number(this.selectedTickValue || this.inputSliderValue));
  };

  private setInitialValue() {
    /* Logic to set a passed in starting value and the input slider value that corresponds to the initial start value */
    if (this.ticks) {
      this.selectedTickValue = this.startValue ? this.startValue : this.ticksArray[0];
      this.inputSliderValue = this.startValue ? this.ticksArray.indexOf(this.startValue) : 0;
    } else if (typeof this.min === 'number') {
      this.selectedTickValue = this.startValue ? this.startValue : this.min;
      this.inputSliderValue = this.selectedTickValue;
    }
    this.startValue = undefined;
  }

  componentWillLoad() {
    if (this.ticks) this.parseArrayProp(this.ticks);

    this.setInitialValue();
  }

  componentDidRender() {
    if (this.ticksArray.length !== 0) this.shouldRenderTicks = true;
    this.setProgressBar();
  }

  componentDidUpdate() {
    if (this.startValue) {
      this.setInitialValue();
    }
  }

  private renderTicks = () => {
    // The math for ticks is a little tricky because they must be contained
    // within the track length.
    const inputWidth = this.inputElement?.clientWidth || 100;

    return (
      <div class="aha-slider__ticks-container">
        {this.ticksArray.length !== 0 &&
          inputWidth &&
          this.ticksArray.map((tickValue: any, index: number) => {
            return (
              <span
                key={tickValue}
                part="tick"
                class="aha-slider__tick"
                style={{
                  backgroundColor:
                    index <= this.inputSliderValue
                      ? 'var(--aha-slider--track-color-left)'
                      : 'var(--aha-slider--track-color-right)',
                }}
              />
            );
          })}
      </div>
    );
  };

  render() {
    let labelContent = null;
    if (this.labeled) {
      labelContent = (
        <div
          class={`aha-slider__info aha-slider__info--${
            this.labeled === 'above' ? 'above' : 'below'
          }`}
        >
          <slot name="label"></slot>
          <div class="aha-slider__info-unit-label">
            {this.selectedTickValue || this.inputSliderValue}
            <slot name="units"></slot>
          </div>
        </div>
      );
    }

    return (
      <Host>
        <div class="aha-slider__layout">
          {this.labeled === 'above' && labelContent}
          <div class="aha-slider__container">
            <input
              type="range"
              part="input"
              class="aha-slider__input"
              ref={ref => (this.inputElement = ref as HTMLElement)}
              min={this.ticksArray.length !== 0 ? 0 : this.min}
              max={this.ticksArray.length !== 0 ? this.ticksArray.length - 1 : this.max}
              step={this.step || 1}
              value={this.inputSliderValue}
              onInput={this.handleInput}
            />
            {this.shouldRenderTicks && this.renderTicks()}
          </div>
          {this.labeled === 'below' && labelContent}
        </div>
      </Host>
    );
  }
}
